import { useEffect, useState } from "react";
import { AcademyApiError, academyRequest, academyVersionRequest } from "./academyApi";
import { useIdentity } from "./useIdentity";
import type { ParticipationProjection, EntitlementDecision } from "./generated/participation";
export function useParticipation(enabled = true, entity = false) {
  const { session } = useIdentity();
  const context = `${session?.tenantId}:${session?.user.id}:${session?.workspace.id}:${entity}`;
  const [state, setState] = useState<{ context: string; data?: ParticipationProjection; error?: string }>();
  const [revision, refresh] = useState(0);
  const present = Boolean(session);
  useEffect(() => {
    if (!enabled || !present) return;
    const abort = new AbortController();
    setState(undefined);
    void academyRequest<ParticipationProjection>(
      `participation/${entity ? "entity" : "me"}`,
      undefined,
      "GET",
      true,
      undefined,
      abort.signal,
    )
      .then(data => {
        if (!abort.signal.aborted) setState({ context, data });
      })
      .catch(error => {
        if (!abort.signal.aborted) setState({ context, error: error.message });
      });
    return () => abort.abort();
  }, [context, enabled, entity, present, revision]);
  const current = state?.context === context ? state : undefined;
  return {
    data: current?.data,
    error: current?.error || (!present ? "Sign in to load participation records." : ""),
    loading: enabled && present && !current,
    refresh: () => refresh(n => n + 1),
  };
}
export function checkParticipation(enrollmentId: string, scopeKind: "program" | "lab", scopeKey: string) {
  return academyRequest<EntitlementDecision>(
    `participation/me/entitlement-check?${new URLSearchParams({ enrollmentId, scopeKind, scopeKey })}`,
  );
}
const labsHandoffKeys = new Map<string, string>();
export async function requestLabsHandoff(enrollmentId: string, labKey: string) {
  const fingerprint = `${enrollmentId}:${labKey}`,
    key = labsHandoffKeys.get(fingerprint) ?? crypto.randomUUID();
  labsHandoffKeys.set(fingerprint, key);
  try {
    const result = await academyVersionRequest<{
      status: "ready";
      destinationUrl: string;
      expiresAt: string;
    }>("v3/integrations/labs/handoffs", { enrollmentId, labKey }, "POST", true, key);
    labsHandoffKeys.delete(fingerprint);
    return result;
  } catch (error) {
    if (error instanceof AcademyApiError) labsHandoffKeys.delete(fingerprint);
    throw error;
  }
}
const classHandoffKeys = new Map<string, string>();
export async function requestClassHandoff(
  contextKind: "learning_session" | "exam_session",
  contextId: string,
  mode: "join" | "host" | "rehearsal" | "proctor",
) {
  const fingerprint = `${contextKind}:${contextId}:${mode}`,
    key = classHandoffKeys.get(fingerprint) ?? crypto.randomUUID();
  classHandoffKeys.set(fingerprint, key);
  try {
    const result = await academyVersionRequest<{
      status: "ready";
      destinationUrl: string;
      expiresAt: string;
    }>("v3/integrations/class/handoffs", { contextKind, contextId, mode }, "POST", true, key);
    classHandoffKeys.delete(fingerprint);
    return result;
  } catch (error) {
    if (error instanceof AcademyApiError) classHandoffKeys.delete(fingerprint);
    throw error;
  }
}
export const isParticipationDirectory = (route: string) => ["people-learners"].includes(route);
